import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const StudentRegistration = () => {
  const navigate = useNavigate();

  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [requiresApproval, setRequiresApproval] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    className: '',
    age: '',
    parentContact: '',
    address: '',
    username: '',
    password: '',
    confirmPassword: ''
  });

  // Load classes and check admin permissions on component mount
  useEffect(() => {
    fetchClasses();
    checkAdminPermissions();
  }, []);

  // Check if current admin has permission to access this page
  useEffect(() => {
    if (currentAdmin) {
      // All admin roles can access student registration, but with different workflows
      // Super admin: direct registration
      // Others: approval workflow
      // No access restrictions at page level
    }
  }, [currentAdmin]);

  const checkAdminPermissions = () => {
    const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
    setCurrentAdmin(adminUser);

    // Management admins need approval for student registration
    if (adminUser.role === 'management_admin') {
      setRequiresApproval(true);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.CLASSES}`);
      if (!response.ok) throw new Error('Failed to fetch classes');
      const classesData = await response.json();
      setClasses(classesData);
    } catch (error) {
      console.error('Error fetching classes:', error);
      setErrors({ general: 'Failed to load classes. Please refresh the page.' });
    } finally {
      setLoadingClasses(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear specific field errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Debug logging
    console.log('Validating form data:', formData);

    // Required fields validation
    const requiredFields = ['firstName', 'lastName', 'className', 'age', 'parentContact', 'username', 'password'];
    requiredFields.forEach(field => {
      const value = formData[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')} is required`;
      }
    });

    // Age validation
    if (formData.age) {
      const ageNum = parseInt(formData.age);
      if (isNaN(ageNum) || ageNum < 3 || ageNum > 25) {
        newErrors.age = 'Age must be between 3 and 25';
      }
    }

    // Username validation
    if (formData.username && (formData.username.length < 3 || !/^[a-zA-Z0-9_]+$/.test(formData.username))) {
      newErrors.username = 'Username must be at least 3 characters and contain only letters, numbers, and underscores';
    }

    // Password validation
    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Parent contact validation (basic phone/email check)
    if (formData.parentContact && !/^[^\s@]+@[^\s@]+\.[^\s@]+$|^[\+]?[1-9][\d]{0,15}$/.test(formData.parentContact)) {
      newErrors.parentContact = 'Please enter a valid email or phone number';
    }

    console.log('Validation errors:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before proceeding
    const isValid = validateForm();
    if (!isValid) {
      setErrors(prev => ({
        ...prev,
        general: 'Please fix the errors in the form before submitting.'
      }));
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Remove confirmPassword before processing
      const { confirmPassword, ...studentData } = formData;

      if (requiresApproval) {
        // Management Admin: Create approval request
        const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

        const approvalData = {
          action: 'create',
          resourceType: 'student',
          resourceId: `student-${Date.now()}`, // Temporary ID for approval
          resourceName: `${studentData.firstName} ${studentData.lastName}`,
          reason: `Student registration request for ${studentData.firstName} ${studentData.lastName} (${studentData.className})`,
          studentData: studentData // Include the student data for later processing
        };

        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/admin/approval/requests`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'admin-email': adminUser.email
          },
          body: JSON.stringify(approvalData)
        });

        const data = await response.json();

        if (response.ok) {
          setSuccess(true);
          // Reset form
          setFormData({
            firstName: '',
            lastName: '',
            className: '',
            age: '',
            parentContact: '',
            address: '',
            username: '',
            password: '',
            confirmPassword: ''
          });

          // Show success message for approval request
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        } else {
          setErrors({ general: data.error || 'Failed to submit approval request' });
        }
      } else {
        // Super Admin: Direct registration
        const response = await fetch(`${API_ENDPOINTS.STUDENTS}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(studentData)
        });

        const data = await response.json();

        if (response.ok) {
          setSuccess(true);
          // Reset form
          setFormData({
            firstName: '',
            lastName: '',
            className: '',
            age: '',
            parentContact: '',
            address: '',
            username: '',
            password: '',
            confirmPassword: ''
          });

          // Show success message for 3 seconds, then redirect
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        } else {
          setErrors({ general: data.error || 'Failed to register student' });
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ general: 'Network error. Please check your connection and try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Show success message
  if (success) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-8">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-md">
          <div className="text-6xl mb-4">OK</div>
          <h2 className="text-2xl font-bold text-green-600 mb-4">
            {requiresApproval ? 'Approval Request Submitted!' : 'Registration Successful!'}
          </h2>
          <p className="text-gray-600 mb-6">
            {requiresApproval
              ? 'Your student registration request has been submitted for approval. You will be notified once reviewed.'
              : 'Student has been registered successfully.'
            }<br />
            Redirecting to dashboard...
          </p>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-green-500 h-2 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Register New Student</h1>
            <p className="text-gray-600">Add a new student to the school management system</p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Back to Dashboard
          </button>
        </div>

        {/* General Error Message */}
        {errors.general && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="text-red-500 mr-2">Warning</div>
              <p className="text-red-700">{errors.general}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information Section */}
            <div className="border-b pb-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Profile Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.firstName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter first name"
                  />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.lastName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                </div>

                {/* Age */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Age <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    min="3"
                    max="25"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.age ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter age"
                  />
                  {errors.age && <p className="text-red-500 text-xs mt-1">{errors.age}</p>}
                </div>

                {/* Class */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Class <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="className"
                    value={formData.className}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.className ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loadingClasses}
                  >
                    <option value="">
                      {loadingClasses ? 'Loading classes...' : 'Select Class'}
                    </option>
                    {classes.map((cls, index) => (
                      <option key={index} value={cls}>
                        {cls}
                      </option>
                    ))}
                  </select>
                  {errors.className && <p className="text-red-500 text-xs mt-1">{errors.className}</p>}
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="border-b pb-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Contact Contact Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Parent/Guardian Contact <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="parentContact"
                    value={formData.parentContact}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.parentContact ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Email or phone number"
                  />
                  {errors.parentContact && <p className="text-red-500 text-xs mt-1">{errors.parentContact}</p>}
                  <p className="text-gray-500 text-xs mt-1">Enter email address or phone number for emergency contact</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Home Address
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows="3"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.address ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter complete home address"
                  />
                  {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                  <p className="text-gray-500 text-xs mt-1">Optional: Street address, city, state, postal code</p>
                </div>
              </div>
            </div>

            {/* Account Information Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Security Account Information</h3>
              <div className="space-y-4">
                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.username ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Choose a username"
                  />
                  {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
                  <p className="text-gray-500 text-xs mt-1">Only letters, numbers, and underscores allowed</p>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Create a password"
                  />
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                  <p className="text-gray-500 text-xs mt-1">Must be at least 6 characters long</p>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Confirm your password"
                  />
                  {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={loading || loadingClasses}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 font-medium"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Registering Student...
                  </div>
                ) : (
                  'Register Student'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>All fields marked with <span className="text-red-500">*</span> are required</p>
        </div>
      </div>
    </div>
  );
};

export default StudentRegistration;
